package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.rotate
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span

enum class LSpinnerSize(val pixels: Int) {
    SM(16),
    MD(20),
    LG(24),
}

/**
 * The ring takes its colour from whatever it is nested in, not from the page.
 *
 * It used to paint the track from `borderStrong` and the head from `textSecondary` — greys chosen
 * against a page ground, which is the one ground a spinner is least often on. On a filled primary
 * button the head measured 1.38:1 against the accent in Day and 1.26:1 in Night, so a busy button
 * whose label is blanked showed a solid accent slab with nothing on it; inside an accent badge the
 * same grey put a warm ring beside a green dot and a red one, and the only state that was actually
 * running was the only one without a colour.
 *
 * `currentColor` fixes both from one place. On a button it resolves to the button's foreground
 * (`accentContrast` — 6.93:1 in Day, 5.75:1 in Night), inside a badge to the badge's tone, and
 * standing on a page to `textPrimary`, one step stronger than the grey it replaces. The track is
 * the same colour at low alpha, so the ring stays one object rather than a head over a foreign grey.
 */
val LSpinnerStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.InlineBlock)
        .borderRadius(Radius.Pill)
        .styleModifier {
            property("border-width", "2px")
            property("border-style", "solid")
            property("border-color", "color-mix(in srgb, currentColor 32%, transparent)")
            property("border-top-color", "currentColor")
        }
}

val LSpinnerRotation = Keyframes {
    from { Modifier.rotate(0.deg) }
    to { Modifier.rotate(360.deg) }
}

@Composable
fun LSpinner(
    modifier: Modifier = Modifier,
    size: LSpinnerSize = LSpinnerSize.MD,
    label: String = t("ds.ds.lspinner.loading", "Loading"),
) {
    Span(
        LSpinnerStyle.toModifier()
            .width(size.pixels.px)
            .height(size.pixels.px)
            .animation(
                LSpinnerRotation.toAnimation(
                    duration = 720.ms,
                    timingFunction = AnimationTimingFunction.Linear,
                    iterationCount = AnimationIterationCount.Infinite,
                ),
            )
            .then(modifier)
            .toAttrs {
                if (label.isBlank()) {
                    attr("aria-hidden", "true")
                } else {
                    attr("role", "status")
                    attr("aria-label", label)
                }
            },
    )
}
